import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axiosInstance from '../../axiosInstance';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { useApiKeys } from '../../hooks/useApiKeys';
import Fade from '@mui/material/Fade';
import Backdrop from '@mui/material/Backdrop';
import { useAuth } from 'src/hooks/useAuth';

const OTPModal = ({ open, onClose, onSubmit, loading, onResend }) => {
    const [otp, setOtp] = useState(['', '', '', '']);
    const inputsRef = useRef([]);

    useEffect(() => {
        if (!open) setOtp(['', '', '', '']);
    }, [open]);

    const handleChange = (e, idx) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 1);
        const newOtp = [...otp];
        newOtp[idx] = val;
        setOtp(newOtp);
        if (val && idx < 3) {
            inputsRef.current[idx + 1].focus();
        }
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            inputsRef.current[idx - 1].focus();
        }
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 4).split('');
        if (pasted.length) {
            setOtp([pasted[0] || '', pasted[1] || '', pasted[2] || '', pasted[3] || '']);
            setTimeout(() => {
                const nextIdx = pasted.findIndex((v) => !v);
                if (nextIdx === -1) inputsRef.current[3].focus();
                else inputsRef.current[nextIdx].focus();
            }, 10);
        }
    };

    const otpValue = otp.join('');

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4, width: 350, textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 28, marginBottom: 8 }}>Email Verification</div>
                <div style={{ color: '#757575', fontSize: 16, marginBottom: 24 }}>Enter OTP sent to your mail to confirm transaction</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
                    {[0, 1, 2, 3].map((idx) => (
                        <input
                            key={idx}
                            ref={el => inputsRef.current[idx] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={otp[idx]}
                            onChange={e => handleChange(e, idx)}
                            onKeyDown={e => handleKeyDown(e, idx)}
                            onPaste={handlePaste}
                            style={{
                                width: 56,
                                height: 56,
                                fontSize: 28,
                                textAlign: 'center',
                                border: '1px solid #E0E0E0',
                                borderRadius: 8,
                                outline: 'none',
                                color: '#616161',
                                fontWeight: 600,
                                background: '#fff',
                                transition: 'border 0.2s',
                            }}
                        />
                    ))}
                </div>
                <div style={{ marginBottom: 18 }}>
                    <span onClick={onResend} style={{ color: '#af3585', fontWeight: 500, cursor: 'pointer', fontSize: 15 }}>Resend OTP</span>
                </div>
                <button
                    onClick={() => onSubmit(otpValue)}
                    disabled={loading || otpValue.length !== 4}
                    style={{ width: '100%', padding: '16px', borderRadius: 10, border: 'none', background: (loading || otpValue.length !== 4) ? '#E0E0E0' : '#af3585', color: 'white', fontWeight: 700, fontSize: 18, cursor: (loading || otpValue.length !== 4) ? 'not-allowed' : 'pointer' }}
                >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify'}
                </button>
            </Box>
        </Modal>
    );
};

const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        border: '1px solid #E0E0E0',
        borderRadius: 8,
        padding: '6px',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#BDBDBD'
        }
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#af3585' : state.isFocused ? '#fce4ec' : 'white',
        color: state.isSelected ? 'white' : 'black',
        fontSize: '16px'
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: '16px',
    }),
};

// Add animation CSS
const checkmarkAnim = `@keyframes bounceIn {0%{transform:scale(0.5);opacity:0;}60%{transform:scale(1.2);opacity:1;}80%{transform:scale(0.95);}100%{transform:scale(1);}}
@keyframes drawCircle {0%{stroke-dashoffset: 120;}100%{stroke-dashoffset: 0;}}
@keyframes drawCheck {0%{stroke-dashoffset: 30;}100%{stroke-dashoffset: 0;}}`;

export default function WithdrawFund() {
    const navigate = useNavigate();
    const { PASTACK: { SECRET_KEY } } = useApiKeys();
    const { data: profileData } = useAuth();
    const [amount, setAmount] = useState('');
    const [banks, setBanks] = useState([]);
    const [bankCode, setBankCode] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountName, setAccountName] = useState('');
    const [resolving, setResolving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [otpModal, setOtpModal] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successDetails, setSuccessDetails] = useState({});
    const [bankLoading, setBankLoading] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);

    const bankOptions = banks.map(bank => ({ value: bank.code, label: bank.name }));
    const selectedBankOption = bankOptions.find(option => option.value === bankCode);

    // Prefill bank and account number from profile if available
    useEffect(() => {
        if (profileData) {
            // Shop/merchant
            const profBankCode = profileData.store_bank_code || profileData.bank_code || '';
            const profBankName = profileData.store_bank_name || profileData.bank_name || '';
            const profAccountNumber = profileData.store_account_number || profileData.bank_account_number || '';
            // Only set if not already set (so user can edit)
            if (!bankCode && profBankCode) setBankCode(profBankCode);
            if (!accountNumber && profAccountNumber) setAccountNumber(profAccountNumber);
        }
    }, [profileData]);

    // Fetch banks from Paystack
    useEffect(() => {
        const fetchBanks = async () => {
            setBankLoading(true);
            try {
                const res = await fetch('https://api.paystack.co/bank', {
                    headers: {
                        Authorization: `Bearer ${SECRET_KEY}`,
                    },
                });
                const data = await res.json();
                if (data.status && Array.isArray(data.data)) {
                    setBanks(data.data);
                    setBankCode(data.data[0]?.code || '');
                } else {
                    setBanks([]);
                    setBankCode('');
                }
            } catch (err) {
                setBanks([]);
                setBankCode('');
            }
            setBankLoading(false);
        };
        fetchBanks();
    }, [SECRET_KEY]);

    // Name Enquiry
    useEffect(() => {
        const resolveAccount = async () => {
            if (accountNumber.length === 10 && bankCode) {
                setResolving(true);
                setAccountName('');
                setError('');
                try {
                    const res = await fetch(
                        `https://api.paystack.co/bank/resolve?account_number=${accountNumber}&bank_code=${bankCode}`,
                        {
                            headers: {
                                Authorization: `Bearer ${SECRET_KEY}`,
                            },
                        }
                    );
                    const data = await res.json();
                    if (data.status) {
                        setAccountName(data.data.account_name);
                        setError('');
                    } else {
                        setAccountName('');
                        setError(data.message || 'Could not resolve account details.');
                    }
                } catch (err) {
                    setAccountName('');
                    setError('Could not resolve account details.');
                }
                setResolving(false);
            } else {
                setAccountName('');
                setError('');
            }
        };
        resolveAccount();
    }, [accountNumber, bankCode, SECRET_KEY]);

    // Fetch wallet balance on mount
    useEffect(() => {
        async function fetchBalance() {
            try {
                const res = await axiosInstance.get('/shop/wallet/card?period=today');
                let balance = res.data.data.balance;
                if (typeof balance === 'object' && balance?.$numberDecimal) {
                    balance = Number(balance.$numberDecimal);
                }
                setWalletBalance(Number(balance) || 0);
            } catch {
                setWalletBalance(0);
            }
        }
        fetchBalance();
    }, []);

    // Withdraw Step 1: Initiate
    const handleWithdraw = async () => {
        if (!amount || !accountNumber || !bankCode || !accountName) {
            setError('Please fill all fields correctly.');
            return;
        }
        if (Number(amount) < 100) {
            setError('You cannot initiate withdrawal less than ₦100');
            return;
        }
        if (Number(amount) > 1000) {
            setError('The daily amount you can withdraw from your wallet cannot exceed ₦1000');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const res = await axiosInstance.post('/shop/wallet/initiate-withdrawal', { amount: Number(amount) });
            if (res.status < 300) {
                setOtpModal(true);
            } else {
                setError(res.data.message || 'Unable to initiate withdrawal.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to initiate withdrawal.');
        }
        setSubmitting(false);
    };

    // Withdraw Step 2: OTP
    const handleOtpSubmit = async (otp) => {
        setOtpLoading(true);
        setError('');
        try {
            const res = await axiosInstance.post('/shop/wallet/process-withdrawal', { otp, amount: Number(amount.replace(/,/g, '')) });
            if (res.status < 300) {
                setOtpModal(false);
                setSuccessDetails({
                    amount,
                    bank: banks.find(b => b.code === bankCode)?.name,
                    accountNumber,
                });
                setShowSuccessModal(true);
            } else {
                setError(res.data.message || 'Unable to process withdrawal.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to process withdrawal.');
        }
        setOtpLoading(false);
    };

    const handleResendOtp = () => {
        // TODO: Implement resend OTP logic if available
        // You can call the backend endpoint to resend OTP here
        alert('Resend OTP logic goes here.');
    };

    // Helper to format with commas
    function formatWithCommas(value) {
        if (!value) return '';
        // Remove non-digits
        const num = value.replace(/\D/g, '');
        return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // Validation for amount
    const numericAmount = Number(amount.replace(/,/g, ''));
    const isAmountValid = numericAmount > 0 && numericAmount <= walletBalance && numericAmount >= 100 && numericAmount <= 1000;
    const amountError =
        numericAmount > walletBalance
            ? 'Amount cannot exceed your wallet balance.'
            : numericAmount > 1000
            ? 'The daily amount you can withdraw from your wallet cannot exceed ₦1000'
            : numericAmount < 100 && amount
            ? 'You cannot initiate withdrawal less than ₦100'
            : '';

    return (
        <div style={{ background: "#fff", minHeight: "100vh", fontFamily: "Plus Jakarta Sans, sans-serif", maxWidth: 430, margin: "0 auto", textAlign: 'left' }}>
            <style>{checkmarkAnim}</style>
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <svg onClick={() => navigate(-1)} width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ cursor: 'pointer' }}>
                        <path d="M15 18L9 12L15 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div style={{ fontWeight: 700, fontSize: 22, color: "#222" }}>Withdraw Fund</div>
                <p style={{ color: "#757575", fontSize: 16, margin: 0 }}>Withdraw from your wallet</p>
                <div style={{ marginTop: '40px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#333' }}>Amount</label>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', color: '#757575', fontSize: '16px' }}>₦</span>
                        <input
                            type="text"
                            value={formatWithCommas(amount)}
                            onChange={e => setAmount(e.target.value.replace(/\D/g, ''))}
                            placeholder="1,000.00"
                            style={{ width: '100%', padding: '14px 14px 14px 35px', borderRadius: 8, border: '1px solid #E0E0E0', boxSizing: 'border-box', fontSize: '16px' }}
                        />
                    </div>
                    <p style={{ color: '#757575', fontSize: 12, marginTop: 8 }}>Wallet Balance: ₦{walletBalance.toLocaleString()}</p>
                    {amountError && <p style={{ color: '#F44336', fontSize: 13, marginTop: 4 }}>{amountError}</p>}
                </div>
                <div style={{ marginTop: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#333' }}>Select Bank</label>
                    <Select
                        value={selectedBankOption}
                        onChange={(option) => setBankCode(option.value)}
                        options={bankOptions}
                        styles={customSelectStyles}
                        isLoading={bankLoading}
                        isDisabled={bankLoading || banks.length === 0}
                        placeholder={bankLoading ? "Loading banks..." : "Select Bank"}
                    />
                </div>
                <div style={{ marginTop: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#333' }}>Enter Account Number</label>
                    <input
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="1234567890"
                        maxLength="10"
                        style={{ width: '100%', padding: '14px', borderRadius: 8, border: '1px solid #E0E0E0', boxSizing: 'border-box', fontSize: '16px' }}
                    />
                    {resolving && <p style={{ marginTop: 8, fontSize: 14, color: '#757575' }}>Verifying account...</p>}
                    {accountName && <p style={{ marginTop: 8, fontSize: 14, color: '#333', fontWeight: 500 }}>{accountName}</p>}
                    {error && <p style={{ marginTop: 8, fontSize: 14, color: '#F44336' }}>{error}</p>}
                </div>
                <button onClick={handleWithdraw} disabled={submitting || resolving || !accountName || !isAmountValid} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: (submitting || resolving || !accountName || !isAmountValid) ? '#E0E0E0' : '#af3585', color: 'white', fontWeight: 700, fontSize: 16, marginTop: '35px', cursor: (submitting || resolving || !accountName || !isAmountValid) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Proceed'}
                </button>
            </div>
            <OTPModal open={otpModal} onClose={() => setOtpModal(false)} onSubmit={handleOtpSubmit} loading={otpLoading} onResend={handleResendOtp} />
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={showSuccessModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
                disableEnforceFocus
                sx={{ zIndex: 2000 }}
            >
                <Fade in={showSuccessModal}>
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2001,
                        p: 3,
                        textAlign: 'center',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 24 }}>
                          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" style={{ background: 'none', animation: 'bounceIn 0.7s' }}>
                            <circle
                              cx="55"
                              cy="55"
                              r="44"
                              stroke="#12B76A"
                              strokeWidth="6"
                              fill="#E6F9F0"
                              style={{ strokeDasharray: 120, strokeDashoffset: 0, animation: 'drawCircle 0.7s ease-out forwards' }}
                            />
                            <polyline
                              points="35,60 50,75 78,47"
                              fill="none"
                              stroke="#12B76A"
                              strokeWidth="6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ strokeDasharray: 30, strokeDashoffset: 0, animation: 'drawCheck 0.5s 0.5s ease-out forwards' }}
                            />
                          </svg>
                        </div>
                        <h3 style={{ color: '#12B76A', fontWeight: 700, fontSize: 26, marginBottom: 10 }}>Withdrawal Successful</h3>
                        <p style={{ color: '#333', fontSize: 18, margin: '0 0 30px 0' }}>
                            Withdrawal of <b>₦{Number(successDetails.amount).toLocaleString()}</b> to <b>{successDetails.bank}</b> ({successDetails.accountNumber}) was successful.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: 320, margin: '0 auto' }}>
                            <button
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    borderRadius: 10,
                                    border: 'none',
                                    background: '#af3585',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: 17,
                                    marginBottom: 0,
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setTimeout(() => navigate('/app/wallet/withdraw-fund'), 200);
                                }}
                            >
                                Send Again
                            </button>
                            <button
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    borderRadius: 10,
                                    border: 'none',
                                    background: '#E0E0E0',
                                    color: '#222',
                                    fontWeight: 700,
                                    fontSize: 17,
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setTimeout(() => navigate('/app/wallets'), 200);
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
} 